import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import { Login, Register } from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import { Products, ProductDetail } from "@/pages/Products";
import Invoices from "@/pages/Invoices";
import Gaps from "@/pages/Gaps";
import Purchasing from "@/pages/Purchasing";
import Sales from "@/pages/Sales";
import Team from "@/pages/Team";
import { AcceptInvite, ChangePassword } from "@/pages/Invite";
import Uploads from "@/pages/Uploads";
import Stocktakes from "@/pages/Stocktakes";
import Digest from "@/pages/Digest";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Assistant from "@/pages/Assistant";

const Loading = () => (
  <div className="grid min-h-screen place-items-center bg-white" data-testid="auth-loading">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900" />
  </div>
);

const Protected = ({ children, requireOnboarding = true }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user?.must_change_password) return <Navigate to="/change-password" replace />;
  if (requireOnboarding && !user?.org?.onboarding_complete) return <Navigate to="/onboarding" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to={user?.org?.onboarding_complete ? "/app" : "/onboarding"} replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/onboarding" element={<Protected requireOnboarding={false}><Onboarding /></Protected>} />
          <Route path="/app" element={<Protected><Dashboard /></Protected>} />
          <Route path="/app/products" element={<Protected><Products /></Protected>} />
          <Route path="/app/products/:id" element={<Protected><ProductDetail /></Protected>} />
          <Route path="/app/invoices" element={<Protected><Invoices /></Protected>} />
          <Route path="/app/gaps" element={<Protected><Gaps /></Protected>} />
          <Route path="/app/purchasing" element={<Protected><Purchasing /></Protected>} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/app/team" element={<Protected><Team /></Protected>} />
          <Route path="/app/sales" element={<Protected><Sales /></Protected>} />
          <Route path="/app/uploads" element={<Protected><Uploads /></Protected>} />
          <Route path="/app/stocktakes" element={<Protected><Stocktakes /></Protected>} />
          <Route path="/app/digest" element={<Protected><Digest /></Protected>} />
          <Route path="/app/reports" element={<Protected><Reports /></Protected>} />
          <Route path="/app/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/app/assistant" element={<Protected><Assistant /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
